import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseModalState } from "@src/lib/ui-react";

import { EditPolicyDialog } from "../../components/EditPolicyDialog";
import { PreShiftOrientationSetting } from "../../components/PreShiftOrientationSetting";
import { SettingEditableContent } from "../../components/SettingEditableContent";
import { SettingNumberInput } from "../../components/SettingNumberInput";
import { SettingToggle } from "../../components/SettingToggle";
import { type PreShiftOrientationFormValues } from "../../hooks/useUpdatePreShiftOrientationSetting";
import { type SettingsPermissions } from "../../types";

interface SchedulingSectionViewProps {
  autoApprovePastWorkersEnabled: boolean;
  autoApprovePreviousApprovedWorkersEnabled: boolean;
  isLateCancellationCallNotificationEnabled: boolean;
  isRequestToBookEnabled: boolean;
  isShiftBookedChatNotificationEnabled: boolean;
  isUpdating: boolean;
  lateCancellationCallInstructions: string;
  lateCancellationCallNotificationModalState: UseModalState;
  maxConsecutiveHours: number | null;
  maxConsecutiveHoursEnabled: boolean;
  maxWeeklyHours: number | null;
  maxWeeklyHoursEnabled: boolean;
  permissions: SettingsPermissions;
  preShiftOrientationEnabled: boolean;
  preShiftOrientationLengthInMinutes: number | undefined;
  preShiftOrientationModalState: UseModalState;
  preShiftOrientationQualifications: string[] | undefined;
  lastMinuteThresholdHours: number | null;
  requestToBookDisabled: boolean;
  requestToBookSubtext: string | undefined;
  shouldRebrandToNoDoubles: boolean;
  workplaceId: string;
  workplaceTypeName: string | undefined;
  onAutoApprovePastWorkersToggle: (checked: boolean) => void;
  onAutoApprovePreviousApprovedWorkersToggle: (checked: boolean) => void;
  onLateCancellationCallInstructionsSave: (instructions: string) => void;
  onLateCancellationCallNotificationToggle: (checked: boolean) => void;
  onMaxConsecutiveHoursChange: (value: number) => void;
  onMaxConsecutiveHoursToggle: (checked: boolean) => void;
  onMaxWeeklyHoursChange: (value: number) => void;
  onMaxWeeklyHoursToggle: (checked: boolean) => void;
  onLastMinuteHoursChange: (value: number) => void;
  onLastMinuteHoursToggle: (checked: boolean) => void;
  onPreShiftOrientationSave: (values: PreShiftOrientationFormValues) => void;
  onPreShiftOrientationToggle: (checked: boolean) => void;
  onRequestToBookToggle: (checked: boolean) => void;
  onShiftBookedChatNotificationToggle: (checked: boolean) => void;
}

const CONSECUTIVE_HOURS_LIMIT_DEFAULT = 8;
const WEEKLY_WORK_HOURS_LIMIT_DEFAULT = 40;

export function SchedulingSectionView({
  autoApprovePastWorkersEnabled,
  autoApprovePreviousApprovedWorkersEnabled,
  isLateCancellationCallNotificationEnabled,
  isRequestToBookEnabled,
  isShiftBookedChatNotificationEnabled,
  isUpdating,
  lastMinuteThresholdHours,
  lateCancellationCallInstructions,
  lateCancellationCallNotificationModalState,
  maxConsecutiveHours,
  maxConsecutiveHoursEnabled,
  maxWeeklyHours,
  maxWeeklyHoursEnabled,
  permissions,
  preShiftOrientationEnabled,
  preShiftOrientationLengthInMinutes,
  preShiftOrientationModalState,
  preShiftOrientationQualifications,
  requestToBookDisabled,
  requestToBookSubtext,
  shouldRebrandToNoDoubles,
  workplaceId,
  workplaceTypeName,
  onAutoApprovePastWorkersToggle,
  onAutoApprovePreviousApprovedWorkersToggle,
  onLastMinuteHoursChange,
  onLastMinuteHoursToggle,
  onLateCancellationCallInstructionsSave,
  onLateCancellationCallNotificationToggle,
  onMaxConsecutiveHoursChange,
  onMaxConsecutiveHoursToggle,
  onMaxWeeklyHoursChange,
  onMaxWeeklyHoursToggle,
  onPreShiftOrientationSave,
  onPreShiftOrientationToggle,
  onRequestToBookToggle,
  onShiftBookedChatNotificationToggle,
}: SchedulingSectionViewProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const consecutiveHoursTitle = shouldRebrandToNoDoubles
    ? tu("Prevent professionals from booking double shifts")
    : tu("Block professionals from booking excessive consecutive hours");

  const consecutiveHoursSubtitle = shouldRebrandToNoDoubles
    ? undefined
    : tu("Set the maximum number of consecutive work hours allowed within a 24-hour period.");

  const weeklyHoursTitle = shouldRebrandToNoDoubles
    ? tu("Prevent professionals from booking excessive weekly hours")
    : tu("Block professionals from booking excessive weekly hours");

  const lastMinuteThresholdHoursSubtitle = tu(
    "Workers must request approval to book shifts that start within the number of hours you set."
  );

  return (
    <>
      {permissions.isAdmin && (
        <>
          <SettingToggle
            checked={isRequestToBookEnabled}
            disabled={requestToBookDisabled || isUpdating}
            subtitle={requestToBookSubtext}
            title={tu("Enable Request to Book")}
            onChange={onRequestToBookToggle}
          />
          {isRequestToBookEnabled && (
            <>
              <SettingToggle
                checked={autoApprovePreviousApprovedWorkersEnabled}
                disabled={requestToBookDisabled || isUpdating}
                subtitle={requestToBookSubtext}
                title={tu("Enable Instant Book for previously approved professionals")}
                onChange={onAutoApprovePreviousApprovedWorkersToggle}
              />
              <SettingToggle
                checked={autoApprovePastWorkersEnabled}
                disabled={requestToBookDisabled || isUpdating}
                subtitle={
                  requestToBookSubtext ??
                  "Workers who have previously worked a shift here can instant book without approval."
                }
                title={tu("Enable Instant Book for past workers")}
                onChange={onAutoApprovePastWorkersToggle}
              />
              <SettingToggle
                checked={isDefined(lastMinuteThresholdHours)}
                disabled={requestToBookDisabled || isUpdating}
                subtitle={lastMinuteThresholdHoursSubtitle}
                title={tu("Enable Request to Book for last-minute shifts only")}
                onChange={onLastMinuteHoursToggle}
              />
              {isDefined(lastMinuteThresholdHours) && (
                <SettingNumberInput
                  disabled={requestToBookDisabled || isUpdating}
                  min={1}
                  subtitle={tu(
                    "Set how many hours before a shift starts that Request to Book is required."
                  )}
                  title={tu("Shift starts within")}
                  unit={tu("hours")}
                  value={lastMinuteThresholdHours}
                  onChange={onLastMinuteHoursChange}
                />
              )}
            </>
          )}
        </>
      )}

      <SettingToggle
        checked={maxConsecutiveHoursEnabled}
        disabled={isUpdating}
        subtitle={consecutiveHoursSubtitle}
        title={consecutiveHoursTitle}
        onChange={onMaxConsecutiveHoursToggle}
      />
      {maxConsecutiveHoursEnabled && !shouldRebrandToNoDoubles && (
        <SettingNumberInput
          disabled={isUpdating}
          min={8}
          title={tu("Maximum consecutive hours")}
          unit={tu("hours")}
          value={maxConsecutiveHours ?? CONSECUTIVE_HOURS_LIMIT_DEFAULT}
          onChange={onMaxConsecutiveHoursChange}
        />
      )}

      <SettingToggle
        checked={maxWeeklyHoursEnabled}
        disabled={isUpdating}
        subtitle={tu("Set the maximum number of work hours allowed per week (Sunday to Saturday).")}
        title={weeklyHoursTitle}
        onChange={onMaxWeeklyHoursToggle}
      />
      {maxWeeklyHoursEnabled && (
        <SettingNumberInput
          disabled={isUpdating}
          max={168}
          min={8}
          title={tu("Maximum weekly hours")}
          unit={tu("hours")}
          value={maxWeeklyHours ?? WEEKLY_WORK_HOURS_LIMIT_DEFAULT}
          onChange={onMaxWeeklyHoursChange}
        />
      )}

      <SettingToggle
        checked={isShiftBookedChatNotificationEnabled}
        disabled={isUpdating}
        subtitle={tu(
          "When enabled, booking a shift adds an unread message to your chat so your team is notified. When off, the chat updates silently without a notification."
        )}
        title={tu("Get notified when a shift is booked")}
        onChange={onShiftBookedChatNotificationToggle}
      />

      {permissions.isAdmin && (
        <>
          <SettingToggle
            checked={preShiftOrientationEnabled}
            disabled={isUpdating}
            subtitle={tu(
              "When enabled, a professional's first shift at this workplace starts earlier to include time for orientation."
            )}
            title={tu("Start of Shift Orientation")}
            onChange={onPreShiftOrientationToggle}
          />
          <PreShiftOrientationSetting
            initialOrientationLengthInMinutes={preShiftOrientationLengthInMinutes}
            initialQualifications={preShiftOrientationQualifications}
            isEnabled={preShiftOrientationEnabled}
            isSaving={isUpdating}
            modalState={preShiftOrientationModalState}
            workplaceId={workplaceId}
            workplaceTypeName={workplaceTypeName}
            onSave={onPreShiftOrientationSave}
          />

          <SettingToggle
            checked={isLateCancellationCallNotificationEnabled}
            disabled={isUpdating}
            subtitle={tu(
              "When enabled, Clipboard will call the specified number per the instructions below if a professional cancels within 8 hours of the shift start time or no-shows."
            )}
            title={tu("Late-cancellation call notification")}
            onChange={onLateCancellationCallNotificationToggle}
          />
          {isLateCancellationCallNotificationEnabled && (
            <SettingEditableContent
              buttonLabel={tu("Edit call instructions")}
              disabled={isUpdating}
              subtitle={lateCancellationCallInstructions}
              title={tu("Late-cancellation call instructions")}
              onEdit={lateCancellationCallNotificationModalState.openModal}
            />
          )}
          <EditPolicyDialog
            fieldName={tu("Call instructions")}
            initialContent={lateCancellationCallInstructions}
            isSaving={isUpdating}
            maxCharacters={500}
            minCharacters={1}
            modalState={lateCancellationCallNotificationModalState}
            title={tu("Review and edit late-cancellation call instructions")}
            onSave={onLateCancellationCallInstructionsSave}
          />
        </>
      )}
    </>
  );
}
